import { motion } from "framer-motion";
import { useTheme } from "@/components/ui/theme-provider";

interface ProgressBarProps {
  currentQuestion: number;
  totalQuestions: number;
}

export const ProgressBar = ({ currentQuestion, totalQuestions }: ProgressBarProps) => {
  // Use integer arithmetic to avoid floating point issues completely
  const progressPercentage = ((currentQuestion + 1) | 0) * 100 / (totalQuestions | 0) | 0;
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  return (
    <div className="mb-8">
      <h2 className="text-2xl font-bold mb-2 text-foreground">
        Question {currentQuestion + 1} of {totalQuestions}
      </h2>

      {/* Progress bar container */}
      <div className={`w-full ${isDarkMode ? 'bg-[#121a2e]' : 'bg-gray-100'} rounded-full h-2 relative overflow-hidden`}>
        {/* Main progress fill */}
        <motion.div
          className="absolute top-0 left-0 h-full bg-gradient-to-r from-green-500 to-green-400 rounded-full"
          style={{
            width: `${progressPercentage}%`,
          }}
          transition={{ duration: 0.3 }}
        >
          {/* Shine effect */}
          <motion.div
            className="absolute inset-0 w-[50%] bg-gradient-to-r from-transparent via-white/30 to-transparent"
            animate={{
              x: ['-100%', '200%'],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
              repeatDelay: 0.5
            }}
          />

          {/* Edge glow */}
          <motion.div
            className="absolute top-0 h-full w-4 rounded-r-full bg-white/50"
            style={{ left: 'calc(100% - 8px)' }}
            animate={{
              opacity: [0.5, 0.8, 0.5],
              scale: [0.8, 1, 0.8]
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />

          {/* Small moving dots */}
          <div className="absolute inset-y-0 left-0 w-full overflow-hidden">
            <motion.div 
              className="absolute top-1/2 h-1 w-1 rounded-full bg-white/70"
              style={{ y: '-50%' }}
              animate={{
                x: ['0%', '100%'],
                opacity: [0, 1, 0]
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: 'linear',
                repeatDelay: 1
              }}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
};